/**
 * Keep many-to-many links in sync from either side.
 * e.g. project.collaboratorIds <-> collaborator.projectIds
 *      project.menteeIds <-> mentee.projectIds
 *
 * Pass `context` after an edit so removals stick (edited side is source of truth).
 * Without context (save/download), people.projectIds are canonical and project lists
 * are rebuilt from them (after absorbing any project-only links into people).
 */

const cloneDraftPeople = (draft) => {
  const projects = (draft.phdResearchProjects || []).map((p) => ({
    ...p,
    collaboratorIds: [...(p.collaboratorIds || [])],
    menteeIds: [...(p.menteeIds || [])],
  }));
  const collaborators = (draft.collaborators || []).map((c) => ({
    ...c,
    projectIds: [...(c.projectIds || [])],
  }));
  const mentoring = (draft.mentoring || []).map((m) => ({
    ...m,
    projectIds: [...(m.projectIds || [])],
  }));

  return {
    projects,
    collaborators,
    mentoring,
    projectById: Object.fromEntries(projects.map((p) => [p.id, p])),
    collabById: Object.fromEntries(collaborators.map((c) => [c.id, c])),
    menteeById: Object.fromEntries(mentoring.map((m) => [m.id, m])),
  };
};

const setMembership = (list, id, shouldHave) => {
  const has = list.includes(id);
  if (shouldHave && !has) list.push(id);
  if (!shouldHave && has) {
    const idx = list.indexOf(id);
    if (idx >= 0) list.splice(idx, 1);
  }
};

export const syncProjectPeopleLinks = (draft, context) => {
  const {
    projects,
    collaborators,
    mentoring,
    projectById,
    collabById,
    menteeById,
  } = cloneDraftPeople(draft);

  if (context?.from === "person" && context.role === "mentee") {
    const personId = context.personId;
    const desired = new Set(context.projectIds || []);
    const mentee = menteeById[personId];
    if (mentee) mentee.projectIds = [...desired];

    projects.forEach((project) => {
      setMembership(project.menteeIds, personId, desired.has(project.id));
    });
  } else if (context?.from === "person" && context.role === "collaborator") {
    const personId = context.personId;
    const desired = new Set(context.projectIds || []);
    const collab = collabById[personId];
    if (collab) collab.projectIds = [...desired];

    projects.forEach((project) => {
      setMembership(project.collaboratorIds, personId, desired.has(project.id));
    });
  } else if (context?.from === "project") {
    const projectId = context.projectId;
    const desiredMentees = new Set(context.menteeIds || []);
    const desiredCollabs = new Set(context.collaboratorIds || []);
    const project = projectById[projectId];
    if (project) {
      project.menteeIds = [...desiredMentees];
      project.collaboratorIds = [...desiredCollabs];
    }

    mentoring.forEach((mentee) => {
      setMembership(mentee.projectIds, projectId, desiredMentees.has(mentee.id));
    });
    collaborators.forEach((collab) => {
      setMembership(
        collab.projectIds,
        projectId,
        desiredCollabs.has(collab.id),
      );
    });
  } else {
    // Full sync: absorb project-only edges into people, then rebuild project lists.
    projects.forEach((project) => {
      (project.menteeIds || []).forEach((mid) => {
        const mentee = menteeById[mid];
        if (mentee) setMembership(mentee.projectIds, project.id, true);
      });
      (project.collaboratorIds || []).forEach((cid) => {
        const collab = collabById[cid];
        if (collab) setMembership(collab.projectIds, project.id, true);
      });
    });

    projects.forEach((project) => {
      project.menteeIds = mentoring
        .filter((m) => m.projectIds.includes(project.id))
        .map((m) => m.id);
      project.collaboratorIds = collaborators
        .filter((c) => c.projectIds.includes(project.id))
        .map((c) => c.id);
    });
  }

  return {
    ...draft,
    phdResearchProjects: projects,
    collaborators,
    mentoring,
  };
};

/** Resolve people linked to a project from either direction */
export const resolveProjectCollaborators = (project, collaboratorsList) => {
  if (!project) return [];
  const fromProject = new Set(project.collaboratorIds || []);
  const fromPeople = collaboratorsList.filter((c) =>
    (c.projectIds || []).includes(project.id),
  );
  fromPeople.forEach((c) => fromProject.add(c.id));
  return [...fromProject]
    .map((id) => collaboratorsList.find((c) => c.id === id))
    .filter(Boolean);
};

export const resolveProjectMentees = (project, mentoringList) => {
  if (!project) return [];
  const fromProject = new Set(project.menteeIds || []);
  const fromPeople = mentoringList.filter((m) =>
    (m.projectIds || []).includes(project.id),
  );
  fromPeople.forEach((m) => fromProject.add(m.id));
  return [...fromProject]
    .map((id) => mentoringList.find((m) => m.id === id))
    .filter(Boolean);
};

const clonePillarGraph = (draft) => {
  const pillars = (draft.researchPillars || []).map((p) => ({
    ...p,
    associations: { ...(p.associations || {}) },
  }));
  const projects = (draft.phdResearchProjects || []).map((p) => ({
    ...p,
    pillarIds: [...(p.pillarIds || [])],
  }));
  const publications = (draft.publications || []).map((p) => ({
    ...p,
    pillarIds: [...(p.pillarIds || [])],
  }));
  const newsItems = (draft.newsItems || []).map((n) => ({
    ...n,
    pillarIds: [...(n.pillarIds || [])],
  }));
  const features = (draft.features || []).map((f) => ({
    ...f,
    pillarIds: [...(f.pillarIds || [])],
  }));

  return {
    pillars,
    projects,
    publications,
    newsItems,
    features,
    pillarById: Object.fromEntries(pillars.map((p) => [p.id, p])),
  };
};

const setAssocList = (pillar, key, values) => {
  if (!pillar.associations) pillar.associations = {};
  if (!values.length) {
    delete pillar.associations[key];
    if (Object.keys(pillar.associations).length === 0) {
      delete pillar.associations;
    }
    return;
  }
  pillar.associations[key] = values;
};

/**
 * Keep pillar <-> content links in sync.
 * - projects use id
 * - publications / news / features use title
 *
 * `pillar.associations.{projects,publications,news,features}` mirrors
 * each item's `pillarIds` (what the site modal actually reads).
 */
export const syncPillarContentLinks = (draft, context) => {
  const {
    pillars,
    projects,
    publications,
    newsItems,
    features,
    pillarById,
  } = clonePillarGraph(draft);

  const applyPillarAsSource = (pillarId, assoc = {}) => {
    const desiredProjects = new Set(assoc.projects || []);
    const desiredPubs = new Set(assoc.publications || []);
    const desiredNews = new Set(assoc.news || []);
    const desiredFeatures = new Set(assoc.features || []);

    projects.forEach((project) => {
      setMembership(project.pillarIds, pillarId, desiredProjects.has(project.id));
    });
    publications.forEach((pub) => {
      setMembership(pub.pillarIds, pillarId, desiredPubs.has(pub.title));
    });
    newsItems.forEach((item) => {
      setMembership(item.pillarIds, pillarId, desiredNews.has(item.title));
    });
    features.forEach((item) => {
      setMembership(item.pillarIds, pillarId, desiredFeatures.has(item.title));
    });
  };

  const applyContentPillarIds = ({
    kind,
    key,
    pillarIds,
  }) => {
    const desired = new Set(pillarIds || []);
    pillars.forEach((pillar) => {
      const shouldHave = desired.has(pillar.id);
      const current = [...(pillar.associations?.[kind] || [])];
      setMembership(current, key, shouldHave);
      setAssocList(pillar, kind, current);
    });
  };

  if (context?.from === "pillar") {
    const pillar = pillarById[context.pillarId];
    if (pillar) {
      // Prefer explicit associations from the edited item
      const assoc = context.associations || pillar.associations || {};
      pillar.associations = { ...assoc };
      applyPillarAsSource(context.pillarId, assoc);
      // Keep association lists aligned with what we just wrote
      setAssocList(
        pillar,
        "projects",
        projects
          .filter((p) => p.pillarIds.includes(context.pillarId))
          .map((p) => p.id),
      );
      setAssocList(
        pillar,
        "publications",
        publications
          .filter((p) => p.pillarIds.includes(context.pillarId))
          .map((p) => p.title),
      );
      setAssocList(
        pillar,
        "news",
        newsItems
          .filter((n) => n.pillarIds.includes(context.pillarId))
          .map((n) => n.title),
      );
      setAssocList(
        pillar,
        "features",
        features
          .filter((f) => f.pillarIds.includes(context.pillarId))
          .map((f) => f.title),
      );
    }
  } else if (context?.from === "project") {
    applyContentPillarIds({
      kind: "projects",
      key: context.projectId,
      pillarIds: context.pillarIds,
    });
    const project = projects.find((p) => p.id === context.projectId);
    if (project) project.pillarIds = [...(context.pillarIds || [])];
  } else if (context?.from === "publication") {
    applyContentPillarIds({
      kind: "publications",
      key: context.title,
      pillarIds: context.pillarIds,
    });
    const pub = publications.find((p) => p.title === context.title);
    if (pub) pub.pillarIds = [...(context.pillarIds || [])];
  } else if (context?.from === "news") {
    applyContentPillarIds({
      kind: "news",
      key: context.title,
      pillarIds: context.pillarIds,
    });
    const item = newsItems.find((n) => n.title === context.title);
    if (item) item.pillarIds = [...(context.pillarIds || [])];
  } else if (context?.from === "feature") {
    applyContentPillarIds({
      kind: "features",
      key: context.title,
      pillarIds: context.pillarIds,
    });
    const item = features.find((f) => f.title === context.title);
    if (item) item.pillarIds = [...(context.pillarIds || [])];
  } else {
    // Full sync: absorb pillar associations into content.pillarIds, then
    // rebuild associations from pillarIds (content side wins after union).
    pillars.forEach((pillar) => {
      const assoc = pillar.associations || {};
      (assoc.projects || []).forEach((pid) => {
        const project = projects.find((p) => p.id === pid);
        if (project) setMembership(project.pillarIds, pillar.id, true);
      });
      (assoc.publications || []).forEach((title) => {
        const pub = publications.find((p) => p.title === title);
        if (pub) setMembership(pub.pillarIds, pillar.id, true);
      });
      (assoc.news || []).forEach((title) => {
        const item = newsItems.find((n) => n.title === title);
        if (item) setMembership(item.pillarIds, pillar.id, true);
      });
      (assoc.features || []).forEach((title) => {
        const item = features.find((f) => f.title === title);
        if (item) setMembership(item.pillarIds, pillar.id, true);
      });
    });

    pillars.forEach((pillar) => {
      setAssocList(
        pillar,
        "projects",
        projects.filter((p) => p.pillarIds.includes(pillar.id)).map((p) => p.id),
      );
      setAssocList(
        pillar,
        "publications",
        publications
          .filter((p) => p.pillarIds.includes(pillar.id))
          .map((p) => p.title),
      );
      setAssocList(
        pillar,
        "news",
        newsItems
          .filter((n) => n.pillarIds.includes(pillar.id))
          .map((n) => n.title),
      );
      setAssocList(
        pillar,
        "features",
        features
          .filter((f) => f.pillarIds.includes(pillar.id))
          .map((f) => f.title),
      );
    });
  }

  return {
    ...draft,
    researchPillars: pillars,
    phdResearchProjects: projects,
    publications,
    newsItems,
    features,
  };
};
